import type { ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, t } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { BannerFactory } from './BannerFactory';
import styles from './y2k.module.css';

const NAV_LINKS = [
  { href: '#steve-top', leaf: S.y2k.nav_home },
  { href: '#steve-qui', leaf: S.y2k.nav_who },
  { href: '#steve-bouton', leaf: S.y2k.banner.nav },
  { href: '#steve-or', leaf: S.y2k.nav_guestbook },
  { href: '#steve-liens', leaf: S.y2k.nav_links },
] as const;

/** Skin 2 — Web 1998 : marquee, compteur six chiffres, livre d'or, webring. */
export function Y2kSkin(): ReactNode {
  const { lang, seen, draft } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();

  const marquee = t(S.y2k.marquee, lang);

  return (
    <div className={styles.root} id="steve-top">
      <div className={styles.marquee}>
        <div className={styles.marqueeTrack}>
          {/* Deux exemplaires : la piste défile de -50 %, la boucle est invisible. */}
          <span>{marquee}</span>
          <span aria-hidden="true">{marquee}</span>
        </div>
      </div>

      <header className={styles.hero}>
        <h1 className={styles.heroTitle}>{t(S.y2k.hero, lang)}</h1>
        <div className={styles.heroSub}>{t(S.y2k.under_construction, lang)}</div>
      </header>

      <div className={styles.body}>
        <nav className={styles.nav}>
          <div className={styles.navTitle}>{t(S.y2k.nav_title, lang)}</div>
          <div className={styles.navLinks}>
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href}>
                {t(link.leaf, lang)}
              </a>
            ))}
          </div>

          <div className={styles.counterBlock}>
            <div className={styles.counterLabel}>{t(S.common.seen_today, lang)}</div>
            {/* Compteur six chiffres, comme tous les compteurs de 1998. */}
            <div className={styles.counter}>{String(seen).padStart(6, '0')}</div>
          </div>

          <button type="button" className={styles.songButton} onClick={actions.toggleAudio}>
            {audio.label}
          </button>
          <p className={styles.audioHint} role="status">
            {audio.hint}
          </p>
        </nav>

        <div className={styles.content}>
          <section id="steve-qui" className={styles.panel}>
            <div className={styles.who}>
              <div className={styles.steveHolder}>
                <Steve bobDuration="2.6s" onClick={actions.bump} label={t(S.common.title, lang)} />
              </div>
              <div className={styles.whoText}>
                <h2 className={cx(styles.panelTitle, styles.titleWho)}>{t(S.y2k.who_title, lang)}</h2>
                <p>{t(S.y2k.who_p1, lang)}</p>
                <p>{t(S.y2k.who_p2, lang)}</p>
              </div>
            </div>
          </section>

          <section className={styles.panel}>
            <h2 className={cx(styles.panelTitle, styles.titleMixer)}>{t(S.y2k.mixer_title, lang)}</h2>

            <VuMeter className={styles.vu} barClassName={styles.vuBar} />

            <div className={styles.pads}>
              {mixer.pads.map((pad) => (
                <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                  <span>{pad.label}</span>
                  <span className={styles.padTime}>{pad.timeLabel}</span>
                </button>
              ))}
            </div>

            <div className={styles.buttons}>
              <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
                {mixer.holdLabel}
              </button>
              <button type="button" className={styles.button} onClick={mixer.onStop}>
                {mixer.stopLabel.toUpperCase()}
              </button>
              <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
                {mixer.wobbleLabel}
              </button>
            </div>

            {mixer.sliders.map((slider) => (
              <div key={slider.key} className={styles.slider}>
                <label className={styles.sliderLabel} htmlFor={`y2k-${slider.key}`}>
                  {slider.label}
                </label>
                <input
                  id={`y2k-${slider.key}`}
                  type="range"
                  className={styles.sliderInput}
                  min={slider.min}
                  max={slider.max}
                  step={slider.step}
                  value={slider.value}
                  onChange={(event) => {
                    slider.onChange(event.currentTarget.valueAsNumber);
                  }}
                />
                <output className={styles.sliderValue} htmlFor={`y2k-${slider.key}`}>
                  {slider.valueLabel}
                </output>
              </div>
            ))}

            <div className={styles.buttons}>
              {mixer.presets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className={styles.button}
                  onClick={preset.onSelect}
                >
                  {preset.label.toUpperCase()}
                </button>
              ))}
            </div>
          </section>

          {/*
            La fonctionnalité propre au skin : le petit bouton clignotant qu'on
            collait en bas de sa page perso, et qu'on échangeait avec les sites
            voisins. Celui-ci sort un vrai fichier.
          */}
          <section id="steve-bouton" className={styles.panel}>
            <h2 className={cx(styles.panelTitle, styles.titleBanner)}>
              {t(S.y2k.banner.title, lang)}
            </h2>
            <BannerFactory lang={lang} />
          </section>

          <section id="steve-or" className={styles.panel}>
            <h2 className={cx(styles.panelTitle, styles.titleGuestbook)}>
              {t(S.y2k.guestbook_title, lang)}
            </h2>
            <ul className={styles.entries}>
              {guestbook.map((entry) => (
                <li key={entry.id} className={styles.entry}>
                  <b>{entry.who}</b> — {entry.text}
                </li>
              ))}
            </ul>
            <div className={styles.compose}>
              <input
                type="text"
                className={styles.input}
                value={draft}
                placeholder={t(S.guestbook.placeholder, lang)}
                aria-label={t(S.guestbook.placeholder, lang)}
                onChange={(event) => {
                  actions.setDraft(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') actions.postEntry();
                }}
              />
              <button type="button" className={styles.send} onClick={actions.postEntry}>
                {t(S.y2k.send, lang)}
              </button>
            </div>
          </section>
        </div>
      </div>

      <footer id="steve-liens" className={styles.footer}>
        {/*
          Le webring de 1998 faisait tourner le visiteur de site en site. Ici
          l'anneau ne compte que trois membres : les trois feuilles de style du
          même site. Les flèches marchent pour de vrai.
        */}
        <div>
          {t(S.y2k.webring, lang)}{' '}
          <button
            type="button"
            className={styles.webringLink}
            onClick={() => {
              actions.cycleSkin(-1);
            }}
          >
            {t(S.y2k.webring_prev, lang)}
          </button>
          {' · '}
          <button
            type="button"
            className={styles.webringLink}
            onClick={() => {
              actions.cycleSkin(0);
            }}
          >
            {t(S.y2k.webring_random, lang)}
          </button>
          {' · '}
          <button
            type="button"
            className={styles.webringLink}
            onClick={() => {
              actions.cycleSkin(1);
            }}
          >
            {t(S.y2k.webring_next, lang)}
          </button>{' '}
          {t(S.y2k.webring_tail, lang)}
        </div>
        <div className={styles.footerCredits}>
          <Credits linkClassName={styles.footerLink} />
        </div>
        <div>{t(S.common.egg_hint, lang)}</div>
      </footer>
    </div>
  );
}
